import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { Box, Divider, Grid, MenuItem, Typography } from '@mui/material';
import { Loader, Page, PageTitleWithBack, SMBSelect } from '../../../components';
import { IDropdownResponse, IEmailTemplateDetails } from '../../../models';
import { getEmailTemplateDetails, getEmailTemplates } from '../../../fetch';
import { EditEmailTemplateForm } from './edit-email-template-form';
import { unCamelCase } from '../../../helpers';

export const ManageEmailTemplatesPage = () => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [isLoadingEmailTemplates, setIsLoadingEmailTemplates] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState<IDropdownResponse[]>([]);
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState('');

  const [isLoadingEmailTemplateDetails, setIsLoadingEmailTemplateDetails] = useState(false);
  const [emailTemplateDetails, setEmailTemplateDetails] = useState<IEmailTemplateDetails | null>(
    null
  );

  const fetchEmailTemplates = async () => {
    try {
      setIsLoadingEmailTemplates(true);
      const res = await getEmailTemplates();
      // Format response into dropdown options for rendering select field
      setEmailTemplates(
        res.map(template => {
          return {
            value: template.emailTemplateId.toString(),
            description: template.templateName,
            shorthand: template.templateName
          };
        })
      );
    } catch (error) {
      enqueueSnackbar(error?.Detail || `Error loading email templates, please try again.`, {
        variant: 'error'
      });
    } finally {
      setIsLoadingEmailTemplates(false);
    }
  };
  useEffect(() => {
    fetchEmailTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchEmailTemplateDetails = async (id: number) => {
    try {
      setIsLoadingEmailTemplateDetails(true);
      const res = await getEmailTemplateDetails(id);
      setEmailTemplateDetails(res);
    } catch (error) {
      enqueueSnackbar(error?.Detail || `Error loading email template details, please try again.`, {
        variant: 'error'
      });
    } finally {
      setIsLoadingEmailTemplateDetails(false);
    }
  };

  return (
    <>
      <Page title="Manage Emails">
        <PageTitleWithBack backText="Back to Admin" backUrl={'/admin'} title="Manage Emails" />
        <Grid container>
          <Grid item xs={12} md={6} xl={4}>
            <SMBSelect
              variant="outlined"
              label={isLoadingEmailTemplates ? 'Loading...' : 'Email Template'}
              name="emailTemplate"
              disabled={isLoadingEmailTemplates}
              value={selectedEmailTemplate}
              onChange={e => {
                setSelectedEmailTemplate(e.target.value);
                if (e.target.value !== '') {
                  fetchEmailTemplateDetails(Number(e.target.value));
                }
              }}
              handleClear={() => {
                setSelectedEmailTemplate('');
                setEmailTemplateDetails(null);
              }}
            >
              {emailTemplates.map(template => {
                return (
                  <MenuItem key={`email-template-${template.value}`} value={template.value}>
                    {unCamelCase(template.description)}
                  </MenuItem>
                );
              })}
            </SMBSelect>
          </Grid>
        </Grid>
        <Divider className={classes.divider} />
        {isLoadingEmailTemplateDetails && <Loader type="inline" position="centered" />}
        {!selectedEmailTemplate && !isLoadingEmailTemplateDetails && (
          <Box my={1} display="flex">
            <Typography>
              Please select an email template to view and edit template details.
            </Typography>
          </Box>
        )}
        {!isLoadingEmailTemplateDetails && emailTemplateDetails && (
          <EditEmailTemplateForm
            emailTemplateDetails={emailTemplateDetails}
            onSubmit={() => {
              fetchEmailTemplateDetails(Number(selectedEmailTemplate)); // refetch on successfil save
            }}
          />
        )}
      </Page>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  divider: {
    margin: `${theme.spacing(1)} 0`
  }
}));
